import { memo, useCallback, useState } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, NewDialog, RowActions } from 'domains/Organizations/Organization/Environments'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { environmentRoute } from 'pages/NWPlatform'
import { environmentsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Environments'
import { environmentQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const navigate = useNavigate({ from: environmentsOverviewRoute.fullPath })
  const { organizationId } = environmentsOverviewRoute.useParams()
  const { query } = environmentsOverviewRoute.useSearch()

  const environmentsQuery = useInfiniteQuery({
    ...environmentQueryKeys.list({ organizationId, query }),
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const environments = environmentsQuery.data?.flat

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:My.Organization.searchEnvironments')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: environmentQueryKeys.list({ organizationId, query }).queryKey,
              }),
            [organizationId, query, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        data={environments ?? []}
        columns={columns}
        isInitialLoadingGlobal={environmentsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={environmentsOverviewRoute.fullPath}
            to={environmentRoute.to}
            params={{ environmentId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', '_manage_environment']}
        enableInfinity
        infinityProps={pick(environmentsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:org.environment', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
